/**
 * The corrections queue — V6-04 §4.
 *
 * Lives beside the leave queue and behaves the same way, because it is the
 * same job done by the same person: somebody says a record is wrong, an
 * approver decides. Live via socket like V4 B-1 — an approver with the page
 * open sees the row arrive and sees it leave when a colleague decides it first.
 */

import { ClipboardCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/shared/EmptyState';
import { UserLink } from '@/components/shared/UserLink';
import { ApiError } from '@/lib/api';
import { useCorrections, useDecideCorrection } from './api';

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function CorrectionsQueue() {
  const { data: rows = [], isLoading } = useCorrections({ status: 'pending' });
  const decide = useDecideCorrection();
  const [busy, setBusy] = useState<string | null>(null);

  async function act(id: string, action: 'approve' | 'reject'): Promise<void> {
    setBusy(id);
    try {
      await decide.mutateAsync({ id, action });
      toast.success(action === 'approve' ? 'Correction approved — the day was re-finalised' : 'Correction rejected');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not decide the correction');
    } finally {
      setBusy(null);
    }
  }

  if (isLoading) return null;

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<ClipboardCheck className="size-5" />}
        title="No attendance corrections waiting"
        description="Requests to fix a recorded workday appear here."
      />
    );
  }

  return (
    <ul className="flex flex-col gap-1" data-testid="corrections-queue">
      {rows.map((correction) => (
        <li
          key={correction._id}
          data-testid="correction-row"
          data-date={correction.date}
          className="vt-surface flex flex-wrap items-center gap-2 px-3 py-2 text-[13px]"
        >
          <UserLink
            userId={correction.userId}
            name={correction.userName}
            avatarUrl={correction.userAvatarUrl ?? undefined}
            size={22}
          />
          <span className="vt-mono text-[12px]">{correction.date}</span>
          <span className="vt-mono text-[12px] vt-muted">
            {timeOf(correction.proposedStartAt)} → {timeOf(correction.proposedEndAt)}
          </span>
          <span className="min-w-0 flex-1 truncate vt-muted">{correction.reason}</span>
          <Badge tone="warn">{correction.status}</Badge>
          <Button
            variant="primary"
            size="sm"
            data-testid="correction-approve"
            disabled={busy === correction._id}
            onClick={() => void act(correction._id, 'approve')}
          >
            {busy === correction._id ? <Loader2 className="animate-spin" /> : null}
            Approve
          </Button>
          <Button
            variant="ghost"
            size="sm"
            data-testid="correction-reject"
            disabled={busy === correction._id}
            onClick={() => void act(correction._id, 'reject')}
          >
            Reject
          </Button>
        </li>
      ))}
    </ul>
  );
}
